import React, { FC, useCallback, useEffect, useState } from 'react';
import { BlockStack, Box, Button, Text, Banner, InlineStack, RadioButton, Link } from '@shopify/polaris';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { EVENTS } from '@/constants/tracking';
import { track } from '@/utils/tracker';
import { type StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { getDownloadUrl } from '@/api/download';
import { useGetVideoPath } from '@/hooks/useGetVideoPath';
import { useBillingContext } from '@/features/billing/components/BillingContext';
import TermsAndConditions from './TermsAndConditions';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import StudioRoutes from '@/utils/routes';
import { getVideoPosterUrl } from '@/utils/videos';

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  video: StudioVideo;
}

enum DownloadType {
  WITH_WATERMARK = 'with-watermark',
  WITHOUT_WATERMARK = 'without-watermark',
}

export const DownloadStudioVideoModal: FC<Props> = ({ video, isOpen, setIsOpen }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { isProPlan } = useBillingContext();
  const [downloadType, setDownloadType] = useState(
    isProPlan ? DownloadType.WITHOUT_WATERMARK : DownloadType.WITH_WATERMARK
  );
  const { pushPath } = useRouterNavigation();
  const videoPath = useGetVideoPath(video);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    const { signedUrl } = await getDownloadUrl({
      s3Key: videoPath,
      /** we store oroginal file in input bucket for StudioVideo and in lambda put a copy into output bucket after post processing  e.g. chaging quality*/
      s3Bucket: isProPlan && video.vodAsset ? 'input' : 'output',
      filename: video.name!,
    });
    await fetch(signedUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const blobURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobURL;
        a.download = video.name!;
        a.click();
      });
    setIsDownloading(false);
    setIsOpen(false);

    track(EVENTS.PUBLISH_DOWLOAD, {
      vodAssetId: video.id,
    });
  }, [videoPath]);

  useEffect(() => {
    if (isOpen) {
      track(EVENTS.DOWNLOAD_MODAL_OPENED, {
        studioVideoId: video.id,
      });
    } else {
      track(EVENTS.DOWNLOAD_MODAL_CLOSED, {
        studioVideoId: video.id,
      });
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onHide={() => setIsOpen(false)}>
      <TitleBar title="Download Video">
        <button onClick={() => setIsOpen(false)}>Cancel</button>
        <button onClick={handleDownload} loading={isDownloading ? 'true' : undefined} variant="primary">
          Download
        </button>
      </TitleBar>
      <Box padding="400">
        <BlockStack gap="800">
          {!isProPlan && (
            <InlineStack gap="800">
              <BlockStack gap="200">
                <Text as="p" variant="headingSm">
                  Select download option:
                </Text>
                <BlockStack gap="100">
                  <RadioButton
                    checked={downloadType === DownloadType.WITH_WATERMARK}
                    label="Download with watermark"
                    onChange={() => setDownloadType(DownloadType.WITH_WATERMARK)}
                  />
                  <RadioButton
                    disabled
                    checked={downloadType === DownloadType.WITHOUT_WATERMARK}
                    label="Download without watermark"
                    onChange={() => setDownloadType(DownloadType.WITHOUT_WATERMARK)}
                    helpText={
                      <Text as="span">
                        <Text as="span" tone="subdued">
                          This option is available using our{' '}
                          <Link onClick={() => pushPath(StudioRoutes.plans())}>Pro Account Plan.</Link>
                        </Text>
                      </Text>
                    }
                  />
                </BlockStack>
              </BlockStack>
              <BlockStack gap="200">
                <Text as="p" variant="headingSm">
                  Preview:
                </Text>

                <div className="relative hidden w-[120px] shrink-0 grow-0 basis-auto self-center overflow-hidden rounded-md shadow-md min-[530px]:block">
                  <img className="w-full" src={getVideoPosterUrl(video)} alt="Video poster" />
                  <img
                    className="absolute left-2 top-1/2 w-1/4 -translate-y-1/2"
                    src="/assets/ai-tools/watermark_bunu.png"
                    alt="Video poster watermark"
                  />
                </div>
              </BlockStack>
            </InlineStack>
          )}
          <TermsAndConditions />
        </BlockStack>
      </Box>
    </Modal>
  );
};
